import React from 'react';
import { Box, Typography } from '@mui/material';

import { v2Colors } from '../../design/theme';

const DISPLAY_FONT = '"Foregen", "Roboto Slab", Georgia, serif';
const BODY_FONT = '"Roboto", "Helvetica Neue", Arial, sans-serif';

const FEATURES = [
  {
    emoji: '💉',
    title: 'Antibiotic-free',
    body: 'No antibiotics, no hormones, no steroids. Ever. Lab-tested for every batch.',
  },
  {
    emoji: '🌾',
    title: 'Free range',
    body: 'Open fields, hydroponic feed, ample farm time. Our chickens are anything but cooped up.',
  },
  {
    emoji: '🐔',
    title: 'No force feeding',
    body: "Just like we don't force-feed our children, we don't force-feed our chickens. Simple as that.",
  },
];

function RaisePillar({ emoji, title, body }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        px: { xs: 0.5, md: 1.5 },
      }}
    >
      <Box
        sx={{
          width: { xs: 72, md: 80 },
          height: { xs: 72, md: 80 },
          borderRadius: '50%',
          border: '1.5px solid',
          borderColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: { xs: 1.5, md: 2 },
        }}
        aria-hidden
      >
        <Typography component="span" sx={{ fontSize: { xs: 36, md: 42 }, lineHeight: 1 }}>
          {emoji}
        </Typography>
      </Box>
      <Typography
        component="h3"
        sx={{
          fontFamily: DISPLAY_FONT,
          fontWeight: 700,
          fontSize: { xs: '1.1rem', md: '1.3rem' },
          lineHeight: 1.2,
          color: v2Colors.white,
          mb: { xs: 1, md: 1.25 },
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: BODY_FONT,
          fontSize: { xs: '0.875rem', md: '0.95rem' },
          lineHeight: 1.55,
          color: 'rgba(255, 255, 255, 0.92)',
          maxWidth: 300,
          mx: 'auto',
        }}
      >
        {body}
      </Typography>
    </Box>
  );
}

export default function HowWeRaiseSection() {
  return (
    <Box
      component="section"
      aria-labelledby="how-we-raise-heading"
      sx={{ mt: { xs: 1, md: 3 }, mb: { xs: 3, md: 4 } }}
    >
      <Box
        sx={{
          bgcolor: v2Colors.primary,
          color: v2Colors.white,
          borderRadius: { xs: 3, md: 3.5 },
          px: { xs: 2.5, sm: 3, md: 4 },
          py: { xs: 3, md: 4 },
          textAlign: 'center',
        }}
      >
        <Typography
          component="p"
          sx={{
            fontFamily: BODY_FONT,
            fontSize: { xs: '0.7rem', md: '0.75rem' },
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'rgba(255, 255, 255, 0.9)',
            mb: { xs: 1, md: 1.25 },
          }}
        >
          — HOW WE RAISE THEM
        </Typography>
        <Typography
          id="how-we-raise-heading"
          component="h2"
          sx={{
            fontFamily: DISPLAY_FONT,
            fontWeight: 700,
            fontSize: { xs: '1.65rem', sm: '1.9rem', md: '2.15rem' },
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            color: v2Colors.white,
            mb: { xs: 3, md: 3.5 },
          }}
        >
          Raised by nature, not needles.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'flex-start' },
            justifyContent: 'center',
            gap: { xs: 3, md: 2 },
          }}
        >
          {FEATURES.map(({ emoji, title, body }) => (
            <RaisePillar key={title} emoji={emoji} title={title} body={body} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
